// Password validation rules - aligned with backend validation-handler.js
export const passwordRules = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

export const validatePassword = (password, confirmPassword) => {
  const errors = [];

  if (password !== confirmPassword) {
    errors.push('Die Passwörter stimmen nicht überein');
  }

  if (password.length < passwordRules.minLength) {
    errors.push(`Dein Passwort muss mindestens ${passwordRules.minLength} Zeichen lang sein`);
  }

  if (passwordRules.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Dein Passwort muss mindestens einen Großbuchstaben enthalten');
  }

  if (passwordRules.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Dein Passwort muss mindestens einen Kleinbuchstaben enthalten');
  }

  if (passwordRules.requireNumber && !/[0-9]/.test(password)) {
    errors.push('Dein Passwort muss mindestens eine Zahl enthalten');
  }

  if (passwordRules.requireSpecialChar) {
    if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Dein Passwort muss mindestens ein Sonderzeichen enthalten');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// The address shape the backend accepts (express-validator's isEmail()): a local
// part, an "@", and a domain whose TLD is at least two characters long. Checking
// this before a request goes out keeps half-typed addresses off the wire — the
// backend answers those with a 422 the user never asked for.
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// True when the value is a complete address that may be sent to the backend.
export const isValidEmail = email => EMAIL_REGEX.test(String(email ?? '').trim());

export const validateEmail = email => {
  if (!email) return { isValid: true, error: null };

  if (!isValidEmail(email)) {
    return { isValid: false, error: 'Bitte gib eine gültige E-Mail-Adresse ein' };
  }
  return { isValid: true, error: null };
};

export const validatePhone = phone => {
  if (!phone) return { isValid: true, error: null };

  // allow strings starting with + and containing only numbers
  const phoneRegex = /^[\+]?[0-9]{6,20}$/;
  if (!phoneRegex.test(phone)) {
    return { isValid: false, error: 'Bitte gib eine gültige Telefonnummer ein' };
  }
  return { isValid: true, error: null };
};

export const validateRequired = (value, fieldName) => {
  if (typeof value === 'boolean') {
    return { isValid: true, error: null };
  }

  if (
    !value ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return { isValid: false, error: `${fieldName} ist erforderlich` };
  }
  return { isValid: true, error: null };
};

export const validateNumber = (value, min = null, max = null) => {
  if (!value) return { isValid: true, error: null };

  const num = Number(value);
  if (isNaN(num)) {
    return { isValid: false, error: 'Bitte gib eine gültige Zahl ein' };
  }

  if (min !== null && num < min) {
    return { isValid: false, error: `Der Wert muss mindestens ${min} sein` };
  }

  if (max !== null && num > max) {
    return { isValid: false, error: `Der Wert darf maximal ${max} sein` };
  }

  return { isValid: true, error: null };
};

export const validateDate = date => {
  if (!date) return { isValid: true, error: null };

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return { isValid: false, error: 'Bitte gib ein gültiges Datum ein' };
  }

  return { isValid: true, error: null };
};

export const validateTime = time => {
  if (!time) return { isValid: true, error: null };

  const str = String(time).slice(0, 5);
  const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/; // HH:MM 24h
  if (!timeRegex.test(str)) {
    return { isValid: false, error: 'Bitte gib eine gültige Uhrzeit im Format HH:MM ein' };
  }
  return { isValid: true, error: null };
};

export const validateZipCode = (zipCode, { allowNonStandard = false } = {}) => {
  if (!zipCode) return { isValid: true, error: null };

  const normalized = String(zipCode).trim();

  // allow numeric postal codes with variable length (3–10 digits)
  const standardZipCodeRegex = /^[0-9]{3,10}$/;
  if (standardZipCodeRegex.test(normalized)) {
    return { isValid: true, error: null };
  }

  if (allowNonStandard) {
    const nonStandardZipCodeRegex = /^[0-9A-Za-z][0-9A-Za-z -]{0,19}$/;
    if (nonStandardZipCodeRegex.test(normalized)) {
      return { isValid: true, error: null };
    }
    return {
      isValid: false,
      error: 'Bitte gib eine gültige Postleitzahl ein (z.B. 12345 oder SW1A 1AA)',
    };
  }

  return { isValid: false, error: 'Bitte gib eine gültige Postleitzahl (3–10 Ziffern) ein' };
};

// Main validation function for a single field
export const validateField = (value, schema) => {
  const errors = [];

  // Required validation
  if (schema.required) {
    const requiredValidation = validateRequired(value, schema.label || 'Feld');
    if (!requiredValidation.isValid) {
      errors.push(requiredValidation.error);
    }
  }

  // Type-specific validation (only if value exists and is not empty)
  if (value && value !== '' && value !== null && value !== undefined) {
    switch (schema.type) {
      case 'email':
        const emailValidation = validateEmail(value);
        if (!emailValidation.isValid) {
          errors.push(emailValidation.error);
        }
        break;
      case 'phone':
        const phoneValidation = validatePhone(value);
        if (!phoneValidation.isValid) {
          errors.push(phoneValidation.error);
        }
        break;
      case 'number':
        const numberValidation = validateNumber(value, schema.min, schema.max);
        if (!numberValidation.isValid) {
          errors.push(numberValidation.error);
        }
        break;
      case 'milliseconds':
        const millisecondsValidation = validateNumber(value, schema.min, schema.max);
        if (!millisecondsValidation.isValid) {
          errors.push(millisecondsValidation.error);
        }
        break;
      case 'date':
        const dateValidation = validateDate(value);
        if (!dateValidation.isValid) {
          errors.push(dateValidation.error);
        }
        break;
      case 'time':
        const timeValidation = validateTime(value);
        if (!timeValidation.isValid) {
          errors.push(timeValidation.error);
        }
        break;
      case 'zipcode':
        const zipCodeValidation = validateZipCode(value, {
          allowNonStandard: schema?.allowNonStandard === true,
        });
        if (!zipCodeValidation.isValid) {
          errors.push(zipCodeValidation.error);
        }
        break;
      default:
        break;
    }
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
};

// Validate entire form
export const validateForm = (formData, schema) => {
  const errors = {};
  let isValid = true;

  Object.keys(schema).forEach(fieldName => {
    const fieldSchema = schema[fieldName];
    const value = formData[fieldName];

    // Skip validation for fields that are hidden or excluded from add
    if (fieldSchema.hideInCreate || fieldSchema.excludeFromAdd) {
      return;
    }

    const validation = validateField(value, fieldSchema);
    if (!validation.isValid) {
      errors[fieldName] = validation.errors;
      isValid = false;
    }
  });

  return { isValid, errors };
};
